import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { productService } from '../../services/productService';

const PersonalizedTipsTab = () => {
  const [loading, setLoading] = useState(false);
  const [tips, setTips] = useState([]);

  useEffect(() => {
    loadTips();
  }, []);

  const loadTips = async () => {
    try {
      setLoading(true);
      const { products } = await productService?.getAll();
      
      // Generate personalized tips based on catalog analysis
      const generatedTips = generatePersonalizedTips(products);
      setTips(generatedTips);
    } catch (error) {
      console.error('Error loading tips:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePersonalizedTips = (products) => {
    const tips = [];

    // 🔍 SEO Tips
    const badTitles = products?.filter(p => 
      !p?.name || p?.name?.length < 30 || p?.name?.length > 80
    )?.length;
    if (badTitles > 0) {
      tips?.push({
        type: 'seo',
        icon: 'Search',
        emoji: '🔍',
        category: 'SEO',
        title: 'Títulos no Mercado Livre: use 60 chars com palavra-chave no início',
        description: `${badTitles} produtos com títulos fora do padrão ideal`,
        affectedCount: badTitles,
        actionable: true,
        color: 'blue'
      });
    }

    // 💰 PREÇO Tips
    const lowPriced = products?.filter(p => p?.price && p?.price < 50)?.length;
    if (lowPriced > 0) {
      tips?.push({
        type: 'pricing',
        icon: 'DollarSign',
        emoji: '💰',
        category: 'PREÇO',
        title: 'Produtos abaixo de R$ 50: considere frete grátis para competir',
        description: `${lowPriced} produtos podem se beneficiar de frete grátis`,
        affectedCount: lowPriced,
        actionable: true,
        color: 'emerald'
      });
    }

    // 📦 ESTOQUE Tips
    const officeCategory = products?.filter(p => 
      p?.category?.toLowerCase()?.includes('escritório') || 
      p?.category?.toLowerCase()?.includes('office') ||
      p?.category?.toLowerCase()?.includes('escolar')
    );
    if (officeCategory?.length > 0) {
      tips?.push({
        type: 'stock',
        icon: 'Package',
        emoji: '📦',
        category: 'ESTOQUE',
        title: 'Itens de escritório: aumentar estoque antes de fevereiro (volta às aulas)',
        description: `${officeCategory?.length} produtos de escritório/escolar no catálogo`,
        affectedCount: officeCategory?.length,
        actionable: false,
        color: 'orange'
      });
    }

    // 🛍️ MARKETPLACE Tips
    const educationalToys = products?.filter(p => 
      (p?.category?.toLowerCase()?.includes('brinquedo') || p?.category?.toLowerCase()?.includes('toy')) &&
      (p?.name?.toLowerCase()?.includes('educativo') || p?.name?.toLowerCase()?.includes('educational'))
    )?.length;
    if (educationalToys > 0) {
      tips?.push({
        type: 'marketplace',
        icon: 'ShoppingBag',
        emoji: '🛍️',
        category: 'MARKETPLACE',
        title: 'Brinquedos educativos: Shopee tem 30% mais conversão que Amazon',
        description: `${educationalToys} brinquedos educativos podem vender melhor na Shopee`,
        affectedCount: educationalToys,
        actionable: true,
        color: 'purple'
      });
    }

    // Additional generic tips if not enough specific ones
    if (tips?.length < 3) {
      tips?.push({
        type: 'seo',
        icon: 'Search',
        emoji: '🔍',
        category: 'SEO',
        title: 'Use palavras-chave de cauda longa para melhor ranqueamento',
        description: 'Títulos específicos convertem 2x melhor que genéricos',
        actionable: false,
        color: 'blue'
      });
    }

    return tips;
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { border: 'border-l-blue-600', bg: 'bg-blue-50', text: 'text-blue-600', iconBg: 'bg-blue-100' },
      emerald: { border: 'border-l-emerald-600', bg: 'bg-emerald-50', text: 'text-emerald-600', iconBg: 'bg-emerald-100' },
      orange: { border: 'border-l-orange-600', bg: 'bg-orange-50', text: 'text-orange-600', iconBg: 'bg-orange-100' },
      purple: { border: 'border-l-purple-600', bg: 'bg-purple-50', text: 'text-purple-600', iconBg: 'bg-purple-100' }
    };
    return colors?.[color] || colors?.blue;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {tips?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Lightbulb" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-foreground font-semibold mb-2">💡 Carregando Dicas...</p>
          <p className="text-muted-foreground text-sm">Analisando seu catálogo para gerar dicas personalizadas.</p>
        </div>
      ) : (
        tips?.map((tip, index) => {
          const colorClasses = getColorClasses(tip?.color);
          return (
            <div 
              key={index} 
              className={`bg-card border-l-4 ${colorClasses?.border} rounded-lg p-4 hover:shadow-brand-md transition-smooth`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 ${colorClasses?.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon name={tip?.icon} size={20} className={colorClasses?.text} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{tip?.emoji}</span>
                    <span className={`text-xs font-bold ${colorClasses?.text} uppercase tracking-wide`}>
                      {tip?.category}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{tip?.title}</h3>
                  <p className="text-sm text-muted-foreground">{tip?.description}</p>
                </div>
              </div>
              
              {tip?.affectedCount && (
                <div className="bg-muted/50 rounded-md px-3 py-2 mb-3">
                  <p className="text-xs text-muted-foreground">
                    📊 <strong>{tip?.affectedCount} produtos</strong> podem se beneficiar
                  </p>
                </div>
              )}
              
              {tip?.actionable && (
                <Button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('statusUpdate', {
                      detail: {
                        type: 'info',
                        message: 'Funcionalidade em desenvolvimento',
                        progress: 100
                      }
                    }));
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <Icon name="Zap" size={16} className="mr-2" />
                  Aplicar Dica
                </Button>
              )}
            </div>
          );
        })
      )}
      
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-4">
        <p className="text-sm text-muted-foreground">
          💡 <strong>Dica Pro:</strong> Implemente essas sugestões para aumentar suas vendas em até 30%!
        </p>
      </div>
    </div>
  );
};

export default PersonalizedTipsTab;